import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import type { CtInterviewSubmissionSummary } from '../../api/types';
import { Pagination } from '../../components/Pagination';
import { clampPage, pageSlice } from '../../components/paginate';
import { useHubContext } from '../../shell/HubContext';
import { SubmissionTable, type SortKey } from './SubmissionTable';

/**
 * Whether a submission matches what was typed.
 *
 * Every whitespace-separated word has to appear somewhere, rather than the whole string having to
 * appear in one field — so "oak jamie" and "jamie oak" both find the same candidate. Same rule as
 * the complimentary care dashboard, for the same reason.
 */
function matchesSearch(submission: CtInterviewSubmissionSummary, terms: string[]): boolean {
  if (terms.length === 0) {
    return true;
  }

  const haystack = `${submission.candidateName} ${submission.location}`.toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

/**
 * The hiring team's view of the CT candidate skills assessment — every completed assessment, most
 * recent first, with a row opening the candidate's answers on their own page.
 *
 * There are no actions on this page, unlike the complimentary care dashboard it borrows its shape
 * from. A submission is a record of what a candidate wrote, not a request waiting on a decision:
 * nothing here is approved, rejected or edited, so the page is a reading surface and says so by
 * having no buttons that change anything.
 */
export function CtInterviewReviewSection() {
  const { api } = useHubContext();
  const navigate = useNavigate();

  const [submissions, setSubmissions] = useState<CtInterviewSubmissionSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('submittedAtUtc');
  const [sortDescending, setSortDescending] = useState(true);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const controller = new AbortController();

    (async () => {
      try {
        setSubmissions(await api.listCtInterviewSubmissions(controller.signal));
      } catch (err) {
        if (!controller.signal.aborted) {
          setLoadError(err instanceof Error ? err.message : 'Could not load assessments.');
        }
      }
    })();

    return () => controller.abort();
  }, [api]);

  const visible = useMemo(() => {
    const terms = search.trim().toLowerCase().split(/\s+/).filter(Boolean);

    return [...(submissions ?? [])]
      .filter((s) => matchesSearch(s, terms))
      .sort((a, b) => {
        // The counts are the numeric columns; comparing them as strings would sort 10 before 9.
        const result =
          sortKey === 'answeredCount' || sortKey === 'correctCount'
            ? a[sortKey] - b[sortKey]
            : String(a[sortKey]).localeCompare(String(b[sortKey]));

        return sortDescending ? -result : result;
      });
  }, [submissions, search, sortKey, sortDescending]);

  // The page number is clamped on render rather than corrected in an effect: the list it indexes
  // into shrinks as the user types, and an effect would paint the empty page once before fixing it.
  const currentPage = clampPage(page, visible.length, pageSize);
  const rows = pageSlice(visible, currentPage, pageSize);

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDescending((d) => !d);
    } else {
      setSortKey(key);
      setSortDescending(false);
    }

    // Sorting reorders the whole list, so page 3 of the old order means nothing in the new one.
    setPage(1);
  }

  function changeSearch(value: string) {
    setSearch(value);
    setPage(1);
  }

  function changePageSize(size: number) {
    // Keep the first row currently on screen visible, rather than jumping back to the top: someone
    // on page 4 asking for more rows wants to see more around where they are, not start over.
    const firstVisibleIndex = (currentPage - 1) * pageSize;
    setPageSize(size);
    setPage(Math.floor(firstVisibleIndex / size) + 1);
  }

  if (loadError) {
    return (
      <div className="page-message page-message--error">
        <h2>Could not load assessments</h2>
        <p>{loadError}</p>
      </div>
    );
  }

  if (submissions === null) {
    return <div className="page-message">Loading…</div>;
  }

  return (
    <div className="care-page care-page--wide">
      <header className="care-page__header">
        <h1 className="care-page__title">Completed CT Candidate Assessments</h1>
        <p className="care-page__subtitle">
          Every skills assessment handed in from the candidate form, most recent first. Open one to
          read the answers.
        </p>
      </header>

      <div className="care-review__toolbar">
        <input
          className="control care-review__search"
          type="search"
          placeholder="Search candidate or location…"
          value={search}
          onChange={(e) => changeSearch(e.target.value)}
        />
        {search.trim().length > 0 && (
          <span className="care-review__count">
            {visible.length} match{visible.length === 1 ? '' : 'es'}
            <button type="button" className="care-document-link" onClick={() => changeSearch('')}>
              clear
            </button>
          </span>
        )}
      </div>

      <section className="care-review__group">
        <div className="care-review__group-header">
          <h2 className="care-review__group-title">
            Assessments
            {submissions.length > 0 && <span className="care-count">{submissions.length}</span>}
          </h2>
        </div>

        <SubmissionTable
          submissions={rows}
          emptyMessage={
            submissions.length === 0
              ? 'No assessments have been handed in yet.'
              : 'No assessments match this search.'
          }
          sortKey={sortKey}
          sortDescending={sortDescending}
          onSort={toggleSort}
          onOpen={(submission) => navigate(`/ct-interview-review/${submission.id}`)}
        />

        <Pagination
          totalItems={visible.length}
          page={currentPage}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={changePageSize}
          itemNoun="assessments"
        />
      </section>
    </div>
  );
}
